import type {
  AttentionItem,
  WorkspaceLocation,
  WorkspaceMode,
  WorkspaceMonitoringReads,
  WorkspaceProjection,
  WorkspaceResearchData,
} from './command-workspace-contract';
import type { Bbox, Candidate, Source } from './fire-signals/contract';
import { STATUS_LABELS } from './fire-signals/contract';
import type { ForecastWatch } from './forecasting';
import type { MonitoringReadState } from './polling';
import { RISK_ROOT, riskHref } from './workbench';

/** Additional query metadata; the frozen presentation interface remains unchanged. */
export interface WorkspaceQueryLocation extends WorkspaceLocation {
  researchSearch: string;
}
const uiKeys = ['mode', 'filter', 'selected', 'areaId', 'view', 'dataSource', 'demoArea'];
function retainedResearch(search: string): URLSearchParams {
  const query = new URLSearchParams(search);
  for (const key of uiKeys) query.delete(key);
  return query;
}
function metadata(location: WorkspaceLocation): string {
  return 'researchSearch' in location && typeof location.researchSearch === 'string'
    ? location.researchSearch
    : '';
}
const filters = {
  monitor: ['all', 'warning', 'forecast'],
  'fire-signals': ['all', 'linked', 'unlinked'],
} as const;
export function parseWorkspaceLocation(search: string): WorkspaceQueryLocation {
  const query = new URLSearchParams(search);
  const rawMode =
    query.get('view') === 'historical'
      ? 'fire-signals'
      : query.get('view') === 'monitoring'
        ? 'monitor'
        : (query.get('mode') ?? 'monitor');
  const mode: WorkspaceMode = rawMode === 'fire-signals' ? 'fire-signals' : 'monitor';
  const rawFilter = query.get('filter') ?? 'all';
  const filter = (filters[mode] as readonly string[]).includes(rawFilter)
    ? (rawFilter as WorkspaceLocation['filter'])
    : 'all';
  const areaId = mode === 'monitor' ? (query.get('areaId') ?? '') : '';
  const areaRef = mode === 'fire-signals' ? (query.get('areaRef') ?? '') : '';
  const selected = query.get('selected') ?? '';
  const uniqueUi = ['mode', 'filter', 'selected', 'areaId'].every(
    (key) => query.getAll(key).length <= 1,
  );
  return {
    mode,
    areaId,
    areaRef,
    filter,
    selected,
    valid:
      (rawMode === 'monitor' || rawMode === 'fire-signals') &&
      rawFilter === filter &&
      uniqueUi &&
      (!areaId || /^[a-z][a-z0-9-]{0,63}$/.test(areaId)) &&
      (!selected ||
        (selected.length <= 512 &&
          !Array.from(selected).some(
            (char) => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127,
          ))),
    researchSearch: retainedResearch(search).toString(),
  };
}
/** Preserve unknown and duplicate research predicates so the existing API can return typed 400. */
export function researchQuery(location: WorkspaceLocation): string {
  if (location.mode !== 'fire-signals') return '';
  const query = retainedResearch(metadata(location));
  // An unchanged duplicate areaRef must reach API validation rather than being repaired.
  if ((query.get('areaRef') ?? '') !== location.areaRef) {
    query.delete('areaRef');
    if (location.areaRef) query.append('areaRef', location.areaRef);
  }
  return query.toString();
}
export function workspaceHref(
  location: WorkspaceLocation,
  patch: Partial<WorkspaceLocation> = {},
): string {
  const next = { ...location, ...patch };
  const changedScope =
    next.mode !== location.mode ||
    next.areaId !== location.areaId ||
    next.areaRef !== location.areaRef;
  if (changedScope && patch.selected === undefined) next.selected = '';
  if (!(filters[next.mode] as readonly string[]).includes(next.filter)) next.filter = 'all';
  const query =
    next.mode === 'fire-signals' ? new URLSearchParams(researchQuery(next)) : new URLSearchParams();
  query.set('mode', next.mode);
  if (next.mode === 'monitor' && next.areaId) query.set('areaId', next.areaId);
  if (next.filter !== 'all') query.set('filter', next.filter);
  if (next.selected) query.set('selected', next.selected);
  return `${RISK_ROOT}?${query}`;
}
function fresh<T>(
  state: MonitoringReadState<T>,
  generatedAt: string | undefined,
  now: number,
): boolean {
  const generated = Date.parse(generatedAt ?? '');
  const read = state.lastReadAt;
  return (
    state.status === 'READY' &&
    state.data !== null &&
    read !== null &&
    Number.isFinite(generated) &&
    Number.isFinite(now) &&
    Number.isFinite(read) &&
    generated <= now &&
    read <= now &&
    now - generated <= 5000 &&
    now - read <= 5000
  );
}
function order(items: AttentionItem[]): AttentionItem[] {
  return items.sort((a, b) => {
    const ta = Date.parse(a.time),
      tb = Date.parse(b.time);
    return (
      (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0) || a.id.localeCompare(b.id)
    );
  });
}
const metricLabel = (metric: string) =>
  metric === 'TEMPERATURE' ? '温度' : metric === 'PARTICULATE' ? '颗粒物' : '指标未知';
const coverageLabels: Record<string, string> = {
  COMPLETE: '覆盖完整',
  COVERAGE_GAP: '覆盖缺口',
  ALL_DISABLED: '全部停用',
  NO_SOURCES: '未配置来源',
};
const healthLabels: Record<string, string> = {
  RECENT: '近期接收',
  STALE: '来源过期',
  NO_DATA: '尚无数据',
  DISABLED: '已停用',
};
/** Exact forecast references, never source-array ordinal guesses. */
export function watchForecastLinks(
  watch: ForecastWatch,
  areaId = watch.areaId,
): { id: string; href: string }[] {
  if (![5, 10, 20].includes(watch.horizonSeconds)) return [];
  return [...new Set(watch.forecastIds)]
    .filter((id) => /^forecast-[a-f0-9]{64}$/.test(id))
    .sort()
    .map((id) => ({
      id,
      href: riskHref(`/forecasts/${encodeURIComponent(id)}`, areaId, {
        horizon: String(watch.horizonSeconds),
      }),
    }));
}
export function projectMonitoring(
  reads: WorkspaceMonitoringReads,
  areaId: string,
  now = Date.now(),
): WorkspaceProjection {
  const summary = reads.summary.data,
    warnings = reads.warnings.data,
    forecast = reads.forecast.data;
  const snapshot = reads.network.data,
    counts = reads.counts.data;
  const scoped = (id: string) => !areaId || id === areaId;
  const known = !areaId || !!summary?.areas.some((a) => a.areaId === areaId);
  const networkKnown = !areaId || !!snapshot?.network.areas.some((a) => a.areaId === areaId);
  const summaryReady = fresh(reads.summary, summary?.generatedAt, now) && known;
  const warningReady = fresh(reads.warnings, warnings?.generatedAt, now);
  const forecastReady = fresh(reads.forecast, forecast?.generatedAt, now) && known;
  const networkReady =
    fresh(reads.network, snapshot?.network.generatedAt, now) &&
    fresh(reads.network, snapshot?.configuration.generatedAt, now) &&
    networkKnown;
  const countReady =
    fresh(reads.counts, counts?.generatedAt, now) && known && (counts?.areaId ?? '') === areaId;
  const activeWarnings = (warnings?.items ?? []).filter(
    (w) => scoped(w.areaId) && w.state === 'ACTIVE',
  );
  const watches = (forecast?.watches ?? []).filter(
    (w) => scoped(w.areaId) && w.status === 'ACTIVE',
  );
  const expectedWarnings = areaId
    ? summary?.areas.find((a) => a.areaId === areaId)?.activeWarningCount
    : summary?.activeWarningCount;
  const completeWarnings =
    warnings !== null &&
    warnings.offset === 0 &&
    warnings.items.length === warnings.total &&
    new Set(warnings.items.map((w) => w.warningId)).size === warnings.items.length;
  const warningMatch = completeWarnings && expectedWarnings === activeWarnings.length;
  const allActiveWatches = forecast?.watches.filter((w) => w.status === 'ACTIVE') ?? [];
  const forecastMatch =
    forecast !== null &&
    forecast.activeWatchCount === allActiveWatches.length &&
    new Set(forecast.watches.map((w) => w.watchId)).size === forecast.watches.length;
  const networkAreas = (snapshot?.network.areas ?? []).filter((a) => scoped(a.areaId));
  const eligible = networkAreas.reduce((sum, a) => sum + a.eligibleSources, 0);
  const enabled = networkAreas.reduce((sum, a) => sum + a.enabledSources, 0);
  const recent = networkAreas.reduce((sum, a) => sum + a.recentSources, 0);
  const items: AttentionItem[] = activeWarnings.map((w) => ({
    id: `warning:${w.warningId}`,
    kind: 'warning',
    areaId: w.areaId,
    title: w.reason === 'MULTI_SOURCE_SUSTAINED_ANOMALY' ? '多来源持续异常' : '活动观测预警',
    time: w.updatedAt,
    status: w.acknowledged ? '本修订已读' : '本修订未读',
    reason:
      w.reason === 'MULTI_SOURCE_SUSTAINED_ANOMALY'
        ? '多类合成来源连续异常，需核对原始观测。'
        : '后端记录发生变化，请查看原始依据。',
    facts: [
      { label: '修订', value: String(w.revision) },
      { label: '支持观测', value: String(w.supportObservationRefs.length) },
      { label: '缺失来源', value: String(w.missingSourceIds.length) },
    ],
    href:
      w.revision > 0 && Number.isSafeInteger(w.revision)
        ? riskHref(`/warnings/${encodeURIComponent(w.warningId)}`, areaId, {
            revision: String(w.revision),
          })
        : null,
    sourceIds: [...w.sourceIds],
  }));
  for (const w of watches) {
    const metrics = [
      ...new Set(
        w.sourceIds.map((id) =>
          metricLabel(
            forecast?.sources.find((s) => s.sourceId === id && s.areaId === w.areaId)?.metric ?? '',
          ),
        ),
      ),
    ];
    items.push({
      id: `forecast:${w.watchId}`,
      kind: 'forecast',
      areaId: w.areaId,
      title: `未来${w.horizonSeconds}秒${metrics.join(' / ') || '指标未知'}关注`,
      time: w.updatedAt,
      status: w.status === 'ACTIVE' ? '活动预测关注' : `历史关注 · ${w.status}`,
      reason:
        w.reason === 'CURRENT_OBSERVED_WARNING'
          ? '当前观测预警下的未来趋势；与观测预警独立。'
          : '已保存的预测提示未来变化；与观测预警独立。',
      facts: [
        { label: '目标时间', value: w.targetAt },
        { label: '预测对象', value: String(w.forecastIds.length) },
        { label: '观测预警关联', value: w.activeObservedWarningId ? '有关联' : '无关联' },
      ],
      href: watchForecastLinks(w, areaId)[0]?.href ?? null,
      sourceIds: [...w.sourceIds],
    });
  }
  order(items);
  const areaIds = new Set([
    ...(summary?.areas.map((a) => a.areaId) ?? []),
    ...(snapshot?.configuration.areas.map((a) => a.areaId) ?? []),
  ]);
  const areas = [...areaIds]
    .filter(scoped)
    .sort()
    .map((id) => {
      const coverage = snapshot?.network.areas.find((a) => a.areaId === id);
      return {
        id,
        name: snapshot?.configuration.areas.find((a) => a.areaId === id)?.displayName ?? id,
        coverage:
          networkReady && coverage
            ? (coverageLabels[coverage.coverage] ?? coverage.coverage)
            : '覆盖未知',
        eligible: networkReady ? (coverage?.eligibleSources ?? null) : null,
        enabled: networkReady ? (coverage?.enabledSources ?? null) : null,
        sources: (snapshot?.configuration.sources ?? [])
          .filter((s) => s.areaId === id)
          .map((s) => {
            const runtime = snapshot?.network.sources.find(
              (r) => r.sourceId === s.sourceId && r.epoch === s.epoch,
            );
            return {
              id: s.sourceId,
              name: s.displayName,
              status: !networkReady
                ? '状态未知'
                : !s.enabled
                  ? '已停用'
                  : (healthLabels[runtime?.receiveHealth ?? ''] ?? '状态未知'),
              epoch: s.epoch,
            };
          })
          .sort((a, b) => a.id.localeCompare(b.id)),
        attentionIds: items.filter((item) => item.areaId === id).map((item) => item.id),
      };
    });
  const current =
    summaryReady &&
    warningReady &&
    forecastReady &&
    networkReady &&
    countReady &&
    warningMatch &&
    forecastMatch;
  const notices = ['合成监测拓扑；区域和来源没有地理坐标。零关注不表示区域安全。'];
  if (!known || !networkKnown) notices.push('区域未知。');
  if (![summaryReady, warningReady, forecastReady, networkReady, countReady].every(Boolean))
    notices.push('数据延迟、读取失败或范围不匹配；保留记录不能视为当前状态。');
  if (!completeWarnings) notices.push('预警列表尚未完整读取。');
  if (!warningMatch || !forecastMatch) notices.push('摘要与列表计数不一致，等待同范围重新读取。');
  return {
    kpis: [
      {
        key: 'active-warning',
        label: '活动观测预警',
        value: summaryReady && warningReady && warningMatch ? (expectedWarnings ?? null) : null,
        note: '合成异常，尚非事故确认',
      },
      {
        key: 'forecast-watch',
        label: '独立预测关注',
        value:
          forecastReady && forecastMatch
            ? watches.filter((w) => w.status === 'ACTIVE').length
            : null,
        note: '与观测预警分别统计',
      },
      {
        key: 'source-coverage',
        label: '来源有效覆盖',
        value: networkReady ? `${eligible} / ${enabled}` : null,
        note: `合格 / 启用；近期接收 ${networkReady ? recent : '未知'}`,
      },
      {
        key: 'pending-evaluation',
        label: '待核对预测',
        value: countReady ? (counts?.pending ?? null) : null,
        note: '缺少实际数据不视为通过',
      },
    ],
    items,
    areas,
    total: current ? items.length : null,
    current,
    context: notices.join(' '),
    bbox: null,
  };
}
function researchItem(c: Candidate, sources: Source[]): AttentionItem {
  const s = c.satellite;
  const regional = c.association.context === 'DISTANT_REGIONAL_CONTEXT';
  return {
    id: c.candidateId,
    kind: c.association.status === 'WEATHER_CONTEXT_LINKED' ? 'linked' : 'unlinked',
    areaId: c.areaRef,
    title: `${STATUS_LABELS[c.association.status]} · ${s.instrument}`,
    time: s.acquiredAtUTC,
    status: '历史热异常 · 火情未核实',
    reason:
      c.association.status === 'WEATHER_CONTEXT_LINKED'
        ? regional
          ? '远距离区域气象背景，非像元现场天气。火情仍未核实。'
          : '已关联气象站区域背景，火情仍未核实。'
        : c.association.status === 'SATELLITE_ONLY'
          ? '仅保留卫星历史热异常，气象背景尚不可用。'
          : '未找到距离、时间与质量均合格的气象背景。',
    facts: [
      { label: '经纬度 EPSG:4326', value: `${s.longitude}, ${s.latitude}` },
      {
        label: '热观测原始量',
        value: `FRP ${s.frpMW === null ? '未知' : `${s.frpMW} MW`}；亮温 ${s.brightnessTi4Kelvin === null ? '未知' : `${s.brightnessTi4Kelvin} K`}`,
      },
      { label: '原生置信等级', value: `${s.nativeConfidence}（非火灾概率）` },
    ],
    href: `/normal-operations/risk-warning/fire-signals/candidates/${encodeURIComponent(c.candidateId)}${c.areaRef ? `?areaRef=${encodeURIComponent(c.areaRef)}` : ''}`,
    sourceIds: sources
      .filter(
        (source) =>
          source.originalHash === c.satellite.originalHash ||
          Object.values(c.weather.metrics).some(
            (metric) => metric?.originalHash === source.originalHash,
          ),
      )
      .map((source) => source.sourceId),
    candidate: c,
  };
}
function bounds(
  data: WorkspaceResearchData,
  areaRef: string,
  candidates: Candidate[],
): Bbox | null {
  const areas = data.summary.areas.filter((a) => !areaRef || a.areaRef === areaRef);
  if (areas.length === 1) return [...areas[0]!.bbox];
  if (areas.length && areas.every((a) => a.bbox[0] < a.bbox[2]))
    return [
      Math.min(...areas.map((a) => a.bbox[0])),
      Math.min(...areas.map((a) => a.bbox[1])),
      Math.max(...areas.map((a) => a.bbox[2])),
      Math.max(...areas.map((a) => a.bbox[3])),
    ];
  if (!candidates.length) return null;
  const longitudes = candidates.map((c) => c.satellite.longitude),
    latitudes = candidates.map((c) => c.satellite.latitude);
  const box: Bbox = [
    Math.min(...longitudes),
    Math.min(...latitudes),
    Math.max(...longitudes),
    Math.max(...latitudes),
  ];
  return box[0] === box[2] || box[1] === box[3] ? null : box;
}
export function projectResearch(data: WorkspaceResearchData, areaRef: string): WorkspaceProjection {
  const candidates = data.candidates.items.filter((c) => !areaRef || c.areaRef === areaRef);
  const known =
    !areaRef || data.summary.areas.some((a) => a.areaRef === areaRef) || candidates.length > 0;
  const linked = candidates.filter((c) => c.association.status === 'WEATHER_CONTEXT_LINKED').length;
  const unlinked = candidates.length - linked;
  const complete =
    data.candidates.offset === 0 &&
    data.candidates.items.length === data.candidates.total &&
    new Set(data.candidates.items.map((c) => c.candidateId)).size === data.candidates.items.length;
  const totals = data.summary.counts;
  // Summary and candidates must be fetched with the same API scope before projection.
  const consistent =
    complete &&
    totals.candidates === data.candidates.total &&
    totals.linked + totals.unlinked === totals.candidates &&
    totals.linked === linked &&
    totals.unlinked === unlinked;
  const sourceComplete =
    data.sources.offset === 0 && data.sources.items.length === data.sources.total;
  const current = known && consistent && sourceComplete;
  const items = order(candidates.map((candidate) => researchItem(candidate, data.sources.items)));
  const context = [
    '历史卫星热异常与气象背景；火情未核实。',
    ...(known ? [] : ['研究区域未知。']),
    ...(complete ? [] : ['候选分页未完整读取。']),
    ...(consistent ? [] : ['摘要与候选范围或计数不一致，等待重新读取。']),
    ...(data.sources.offset === 0 && data.sources.items.length === data.sources.total
      ? []
      : ['来源分页未完整读取。']),
  ].join(' ');
  return {
    kpis: [
      {
        key: 'candidates',
        label: '历史热异常候选',
        value: current ? totals.candidates : null,
        note: '同范围实际候选计数',
      },
      {
        key: 'linked',
        label: '已关联天气背景',
        value: current ? totals.linked : null,
        note: '区域气象背景关联',
      },
      {
        key: 'unlinked',
        label: '未关联天气背景',
        value: current ? totals.unlinked : null,
        note: '保留距离、时间及质量缺口',
      },
      {
        key: 'event-reference',
        label: '独立火情核实',
        value: null,
        note: `未核实；关联参考 ${totals.eventReferences} 条`,
      },
    ],
    items,
    areas: data.summary.areas
      .filter((a) => !areaRef || a.areaRef === areaRef)
      .map((a) => ({
        id: a.areaRef,
        name: a.name,
        coverage: '历史研究范围',
        eligible: null,
        enabled: null,
        sources: [],
        attentionIds: items.filter((i) => i.areaId === a.areaRef).map((i) => i.id),
      })),
    total: current ? totals.candidates : null,
    current,
    context,
    bbox: bounds(data, areaRef, candidates),
  };
}
